import { cn } from "@etudescarre/ui";
import { isNotNil } from "@etudescarre/utils";
import { ClockCountdown as ClockCountdownIcon } from "@phosphor-icons/react";
import type { VersionData } from "./types";

export type SyntheseCellProps = {
  version: VersionData;
  /** Un avis requis est en attente avec délai dépassé sur le document. */
  visaEnRetard: boolean;
  onOpenSynthese: (versionId: number) => void;
  onOpenDestinataires: (
    versionId: number,
    type: "avis" | "coordination",
  ) => void;
  /** Ouvre la grille de transmission interne (formats × équipes). */
  onOpenInterne: (versionId: number) => void;
};

export const VISA_EN_RETARD_LABEL =
  "Avis requis en attente : délai dépassé pour au moins un destinataire";

/** Same words in the list and at the top of the popup. */
export const SYNTHESE_LABELS: Record<string, string> = {
  obs: "Obs",
  ok: "Ok",
  attente: "Attente",
};

const syntheseColors: Record<string, string> = {
  obs: "text-red-600 dark:text-red-400",
  ok: "text-green-600 dark:text-green-400",
  attente: "text-amber-600 dark:text-amber-400",
};

export function SyntheseCell({
  version,
  visaEnRetard,
  onOpenSynthese,
  onOpenDestinataires,
  onOpenInterne,
}: SyntheseCellProps) {
  return (
    <div className="flex items-center gap-2 whitespace-nowrap text-xs">
      {/* Picto "visa en retard" à côté de la synthèse avis */}
      {visaEnRetard && (
        <ClockCountdownIcon
          size={16}
          weight="fill"
          className="shrink-0 text-red-600 dark:text-red-400"
          aria-label={VISA_EN_RETARD_LABEL}
        >
          <title>{VISA_EN_RETARD_LABEL}</title>
        </ClockCountdownIcon>
      )}

      {/* Synthese avis */}
      {isNotNil(version.syntheseAvis) ? (
        <button
          type="button"
          onClick={() => onOpenSynthese(version.id)}
          className={cn(
            "font-medium underline-offset-2 hover:underline",
            syntheseColors[version.syntheseAvis],
          )}
        >
          {SYNTHESE_LABELS[version.syntheseAvis]}
        </button>
      ) : (
        <span className="text-zinc-400">-</span>
      )}

      {/* Diffusion avis count */}
      {isNotNil(version.diffusionAvis) && (
        <button
          type="button"
          onClick={() => onOpenDestinataires(version.id, "avis")}
          className="text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"
        >
          {version.diffusionAvis.received}/{version.diffusionAvis.total}
        </button>
      )}

      {/* Diffusion coordination count */}
      {isNotNil(version.diffusionCoordination) && (
        <button
          type="button"
          onClick={() => onOpenDestinataires(version.id, "coordination")}
          className="ml-1 text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"
        >
          {version.diffusionCoordination.received}/
          {version.diffusionCoordination.total}
        </button>
      )}

      {/* Sous-colonne coordination : nb d'équipes internes (même
          contrat) disposant de la version ; ouvre la grille formats × équipes */}
      {isNotNil(version.diffusionInterne) && (
        <button
          type="button"
          onClick={() => onOpenInterne(version.id)}
          title="Équipes de mon contrat disposant de cette version"
          className="ml-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
        >
          {version.diffusionInterne} éq.
        </button>
      )}
    </div>
  );
}
